import { clientTemplateFits, velocitySupportOf, type ClientModTemplate } from '@mcpanel/core';

/** 省略（その版の既定）を表す select の値 */
const DEFAULT = '__default';

type Props = {
  proxied: boolean;
  setProxied: (value: boolean) => void;
  /** undefined はその版の既定、null は「なし」 */
  clientTemplateId: string | null | undefined;
  setClientTemplateId: (value: string | null | undefined) => void;
  templates?: ClientModTemplate[];
  /** 今の hub（Velocity で最初に入るサーバー）。まだなければ undefined */
  hub?: string;
  isModded: boolean;
  /** Velocity（hub 経由）に置けるかはソフトで決まる（Paper・Purpur・Fabric） */
  family: string;
  mcVersion: string;
  name: string;
};

/** hub 配下にするか（直結にするか）と、直結のときのクライアント Mod テンプレート */
export function CreateConnectionFields({
  proxied,
  setProxied,
  clientTemplateId,
  setClientTemplateId,
  templates,
  hub,
  isModded,
  family,
  mcVersion,
  name,
}: Props) {
  const velocity = Boolean(velocitySupportOf(family));
  const underHub = proxied && velocity;
  // 自分が最初の hub になるとき（hub がまだない、または同じ ID）は、自分でテンプレートを選ぶ
  const followsHub = underHub && hub !== undefined && hub !== name;
  const fitting = (templates ?? []).filter((template) => clientTemplateFits(template, mcVersion));
  const fallback = fitting.find((template) => template.defaultForNew && template.mcVersion === mcVersion);
  const value = clientTemplateId === undefined ? DEFAULT : (clientTemplateId ?? '');
  return (
    <section className="card card-body stack" style={{ gap: 10 }}>
      <h2>入り方</h2>
      <label className="check">
        <input type="checkbox" checked={underHub} disabled={!velocity} onChange={(e) => setProxied(e.target.checked)} />
        hub 配下にする（まず hub に入り、/server で移動する）
      </label>
      {underHub ? (
        <p className="small muted" style={{ margin: 0 }}>
          ランチャーには hub だけが出て、このサーバーには hub から移ります。
          {followsHub && <> クライアント Mod は {hub} のテンプレートを使います。</>}
        </p>
      ) : (
        velocity && (
          <p className="small muted" style={{ margin: 0 }}>
            hub を通さず、{name || 'サーバー ID'}.{'<ドメイン>'} で直接つなぎます。ランチャーにも個別に出ます。
          </p>
        )
      )}
      {family === 'fabric' && underHub && (
        <p className="small faint" style={{ margin: 0 }}>
          Velocity 配下の Fabric サーバーには、転送を受ける FabricProxy-Lite（と Fabric API）を自動で入れます。
        </p>
      )}
      {isModded && !velocity && (
        <p className="small faint" style={{ margin: 0 }}>
          NeoForge・Forge などのサーバーは Velocity を通さず、専用のホスト名（{name || 'サーバー ID'}.{'<ドメイン>'}
          ）で直接接続します（hub との行き来でクライアントが失敗しやすいため）。
        </p>
      )}
      {!isModded && !followsHub && (
        <div className="field">
          <label htmlFor="create-client-template">クライアント Mod テンプレート</label>
          <select
            id="create-client-template"
            className="input"
            value={value}
            onChange={(e) =>
              setClientTemplateId(
                e.target.value === DEFAULT ? undefined : e.target.value === '' ? null : e.target.value,
              )
            }
          >
            <option value={DEFAULT}>この版の既定（{fallback?.name ?? 'なし'}）</option>
            <option value="">なし（通常の Minecraft）</option>
            {fitting.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
                {template.mcVersion ? `（${template.mcVersion}）` : ''}
              </option>
            ))}
          </select>
          <span className="small muted">
            プレイヤーのランチャーだけに入る Mod です。作ったあとも「拡張 → ランチャー」で変えられます。
          </span>
        </div>
      )}
    </section>
  );
}
